/** FNV-1a style 8-char hex hash (TZP mini). */
export function fnv1aHash(str) {
  const json = `${JSON.stringify(str)}`;
  let len = json.length;
  let hash = 0x811c9dc5;
  for (let i = 0; i < len; i++) {
    hash = Math.imul(31, hash) + json.charCodeAt(i) | 0;
  }
  return ('0000000' + (hash >>> 0).toString(16)).slice(-8);
}

const GECKO_MARKERS = [
  [DataTransfer, 'mozSourceNode'],
  [Document, 'mozFullScreen'],
  [HTMLCanvasElement, 'mozPrintCallback'],
  [HTMLElement, 'onmozfullscreenerror'],
  [HTMLVideoElement, 'mozDecodedFrames'],
  [IDBIndex, 'mozGetAllKeys'],
  [IDBObjectStore, 'mozGetAll'],
  [Screen, 'mozOrientation'],
  [SVGElement, 'onmozfullscreenchange'],
];

/** True when >5 of 9 moz* prototype markers are present (TZP get_isGecko). */
export function isGeckoEngine() {
  try {
    let found = 0;
    for (const [obj, prop] of GECKO_MARKERS) {
      if (
        typeof obj === 'function' &&
        typeof Object.getOwnPropertyDescriptor(obj.prototype, prop) === 'object'
      ) {
        found++;
      }
    }
    return found > 5;
  } catch {
    return false;
  }
}

function cascadeGeckoVersion() {
  let test;
  try {
    try {
      new Blob().bytes();
    } catch {
      return 127;
    }
    try {
      if (AnimationTimeline.prototype.hasOwnProperty('duration')) return 153;
    } catch {}
    try {
      if (SVGTextPathElement.prototype.hasOwnProperty('side')) return 152;
    } catch {}
    if (CSSContainerRule.prototype.hasOwnProperty('conditions')) return 151;
    if (typeof visualViewport.onscrollend === 'object') return 150;
    try {
      Temporal.PlainDate.from({ calendar: 'gregory', monthCode: 'M12', month: 13, year: 2019, day: 1 });
    } catch (e) {
      if (e?.name === 'RangeError') return 149;
    }
    try {
      if (location.ancestorOrigins !== undefined) return 148;
    } catch {}
    try {
      new Temporal.Duration(0).total({ unit: 'years', relativeTo: '-271821-04-19' });
      return 148;
    } catch {}
    if (Intl.supportedValuesOf('numberingSystem').includes('tols')) return 147;
    try {
      throw new DOMException('a', 'b');
    } catch (e) {
      if (e.columnNumber !== 0) return 146;
    }
    if (new ToggleEvent('toggle', null).source !== undefined) return 145;
    if (window.CSS2Properties === undefined) return 144;
    if (!CSS2Properties.prototype.hasOwnProperty('-moz-appearance')) return 143;
    try {
      const segmenter = new Intl.Segmenter('en', { granularity: 'word' });
      test = Array.from(segmenter.segment('a:b')).map(({ segment }) => segment);
      if (test.length === 3) return 142;
    } catch {}
    try {
      if (Temporal.PlainDate.from('2029-12-31[u-ca=gregory]').weekOfYear === undefined) return 141;
    } catch {}
    try {
      if (IntersectionObserver.prototype.hasOwnProperty('scrollMargin')) return 141;
    } catch {}
    try {
      if (typeof onpointerrawupdate === 'object') return 140;
    } catch {}
    try {
      if (performance.getEntriesByType('paint')[0].presentationTime !== undefined) return 140;
    } catch {}
    try {
      if (document.createElement('audio').preload !== '') return 140;
    } catch {}
    if (HTMLDialogElement.prototype.hasOwnProperty('requestClose')) return 139;
    try {
      if (RTCCertificate.prototype.hasOwnProperty('getFingerprints')) return 138;
    } catch {}
    if (typeof originAgentCluster === 'boolean') return 138;
    try {
      if (HTMLScriptElement.prototype.hasOwnProperty('textContent')) {
        test = Intl.NumberFormat('yo-bj', { style: 'unit', unit: 'year', unitDisplay: 'narrow' }).format(1);
        if (fnv1aHash(test) === '606d1046') return 138;
      }
    } catch {}
    if (typeof Math.sumPrecise === 'function') return 137;
    try {
      if (new globalThis.RegExp('(?i:[A-Z]{4})').test('abcd')) return 136;
    } catch {}
    if (HTMLScriptElement.prototype.hasOwnProperty('textContent')) return 135;
    if (Intl.PluralRules.supportedLocalesOf('lij').join() === 'lij') return 134;
    try {
      const parser = new DOMParser().parseFromString("<select><option name=''></option></select>", 'text/html');
      if (parser.body.firstChild.namedItem('') === null) return 133;
    } catch {}
    try {
      const re = new RegExp('(?:)', 'gv');
      test = RegExp.prototype[Symbol.matchAll].call(re, '𠮷');
      for (let i = 0; i < 3; i++) {
        if (test.next().done === true) return 132;
      }
    } catch {}
    try {
      test = new Intl.DateTimeFormat('zh', { calendar: 'chinese', dateStyle: 'medium' }).format(new Date(2033, 9, 1));
      if (test.slice(0, 4) === '2033') return 131;
    } catch {}
    try {
      new RegExp('[\\00]', 'u');
    } catch (e) {
      if (e + '' === 'SyntaxError: invalid decimal escape in regular expression') return 130;
    }
    if (CSS2Properties.prototype.hasOwnProperty('WebkitFontFeatureSettings')) return 129;
    return 128;
  } catch {
    return 0;
  }
}

function detectGeckoVersion() {
  const version = cascadeGeckoVersion();
  if (version === 153) return '153+';
  else if (version === 127) return '127 or lower';
  return version;
}

/** True when probe (or cached probe) matched Tor Browser. */
function isTor() {
  const paths = [
    'chrome://global/content/torconnect/tor-connect.svg',
    'chrome://global/skin/icons/torbrowser.png',
  ]
  return probeBundleMarkers(paths);
}

/** True when probe (or cached probe) matched Mullvad Browser. */
function isMullvad() {

  const paths = [
    'chrome://global/skin/icons/mullvadbrowser.png',
  ]
  return probeBundleMarkers(paths);
}

/**
 * Probe chrome://global/ icons (TZP get_isBB). Resolves on first onload or when all probes finish.
 */
function probeBundleMarkers(paths) {
  return new Promise((resolve) => {
    let done = false;
    let count = 0;

    const expected = paths.length;

    const timer = setTimeout(() => {
      if (!done) {
        done = true;
        resolve();
      }
    }, 150);

    function finish(result) {
      if (done) return;
      done = true;
      clearTimeout(timer);
      resolve(result);
    }

    for (const path of paths) {
      const img = new Image();
      
      img.onload = function () {
        finish(path);
      };
      img.onerror = function () {
        count++;
        if (count === expected) {
          finish();
        }
      };
      img.src = path;
    }
  });
}

export async function collectGeckoBundleSpecific() {

  if (!isGeckoEngine()) {
    return {};
  }
  const geckoVersion = detectGeckoVersion()

  if (await isMullvad()) {
    return {
      mullvad: { isMullvad: true, geckoVersion },
    }
  }
  if (await isTor()) {
    return {
      tor: { isTor: true, geckoVersion },
    }
  }

  return {};
}